import { useIntl } from 'react-intl';
import { useState } from 'react';
import { ListBox, MenuTrigger, Popover, type Key, type Selection } from 'react-aria-components';
import { FilterButton, Option, Button } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import { IconPlusOutlined } from '@repo/monochrome-icons';
import { ADVANCED_FILTER_KEY } from '../filters-list';
import { LabelListIcon } from '../../label-list-icon/label-list-icon';
import styles from './add-filter-button.module.css';

const ADD_FILTERS_TRACKING_EVENT = 'add_filters_clicked';
const ADD_ADVANCED_FILTER_TRACKING_EVENT = 'add_advanced_filters_clicked';
const SELECT_FILTER_TRACKING_EVENT = 'filter_selected';

interface AddFilterButtonProps {
  hasAdvancedFilters?: boolean;
  options: { label: string; value: string; color?: string }[];
  onSelect: (key: Key | undefined) => void;
}

export function AddFilterButton({ hasAdvancedFilters, options, onSelect }: AddFilterButtonProps) {
  const { formatMessage } = useIntl();
  const segment = useSegment();
  const [open, setOpen] = useState(false);

  const trackFilterSelection = (key: Key) => {
    if (key === ADVANCED_FILTER_KEY) {
      segment.track(ADD_ADVANCED_FILTER_TRACKING_EVENT);
    } else {
      segment.track(SELECT_FILTER_TRACKING_EVENT, { filter_name: key });
    }
  };

  return (
    <MenuTrigger
      isOpen={open}
      onOpenChange={isOpen => {
        if (isOpen) {
          segment.track(ADD_FILTERS_TRACKING_EVENT);
        }
        setOpen(isOpen);
      }}
    >
      <FilterButton className={styles.addFilterButton}>
        <IconPlusOutlined />
        {formatMessage({ id: 'shared-kit.filters.add-filter' })}
      </FilterButton>
      <Popover className={styles.popover}>
        <ListBox
          className={styles.optionList}
          onSelectionChange={(keys: Selection) => {
            const key = Array.from(keys)[0];
            if (!key) return;

            trackFilterSelection(key);
            onSelect(key);
            setOpen(false);
          }}
          selectionMode="single"
        >
          {options.map(option => (
            <Option className={styles.option} id={option.value} key={option.value}>
              {option.color ? (
                <LabelListIcon
                  className={styles.labelColor}
                  data-test-input-label-color
                  labelList={{
                    name: option.label,
                    color: option.color,
                  }}
                />
              ) : null}
              {option.label}
            </Option>
          ))}
        </ListBox>
        {hasAdvancedFilters ? (
          <Button
            className={styles.advancedFilterOption}
            onPress={() => {
              trackFilterSelection(ADVANCED_FILTER_KEY);
              onSelect(ADVANCED_FILTER_KEY);
              setOpen(false);
            }}
            variant="tertiary"
          >
            <IconPlusOutlined />
            {formatMessage({ id: 'shared-kit.filters.add-advanced-filter' })}
          </Button>
        ) : null}
      </Popover>
    </MenuTrigger>
  );
}
